import { useEffect } from 'react';
import { router } from './routes/Routes';
import { RouterProvider } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { darkTheme, theme } from './theme/theme';
import { RootState } from './store/store';
import { useSelector } from 'react-redux';
import { Capacitor } from '@capacitor/core';
import { posthog } from 'posthog-js';
import KeywordPopoversManager from './components/Global/KeywordsPopoverManager';
import {
  getOriginalAdminSession,
  updateCurrentUserInfo,
} from './services/auth';
import { GamepadProvider } from './context/GamepadProvider';
import { initializePushNotifications } from './services/pushNotifications';
import { useSystemTheme } from './hooks/useSystemTheme';
import {
  getOraCookieAcceptCookie,
  setOraCookieAcceptCookie,
} from './utils/cookiesUtils';
import { fetchGeneralUserStats } from './services/stats';
import SubscriptionProvider from './components/SubscriptionProvider';
import { useHotkeys } from 'react-hotkeys-hook';

function getUTMParams() {
  const params = new URLSearchParams(window.location.search);
  const utmData: Record<string, string> = {};

  [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_term',
    'utm_content',
  ].forEach((key) => {
    const value = params.get(key);
    if (value) utmData[key] = value;
  });

  return utmData;
}

function App() {
  const currentUser = useSelector((state: RootState) => state.user.profile);
  const subscription = useSelector(
    (state: RootState) => state.subscription.subscription
  );

  useHotkeys('t', () => {
    if (currentUser?.role === 'admin') {
      if (currentUser.theme === 'dark') {
        updateCurrentUserInfo({ theme: 'light' });
      } else {
        updateCurrentUserInfo({ theme: 'dark' });
      }
    }
  });

  const systemTheme = useSystemTheme();

  // Setup Posthog for authenticated users
  useEffect(() => {
    const setupPosthogUserFeatures = () => {
      if (process.env.NODE_ENV === 'production' && posthog.__loaded) {
        const utmParams = getUTMParams();
        if (Object.keys(utmParams).length > 0) {
          posthog.register_once(utmParams);
        }

        const cookiesAccepted = getOraCookieAcceptCookie();

        if (
          currentUser &&
          currentUser?.email &&
          currentUser?.cohort !== 'rct'
        ) {
          if (cookiesAccepted !== 'accepted') {
            setOraCookieAcceptCookie('accepted');
          }

          // Identify user in PostHog
          posthog.identify(currentUser.id, {
            email: currentUser.email,
            name: currentUser.name,
            created_at: currentUser.createdAt,
            role: currentUser.role,
            isPeerAdvocate: currentUser.ambassador,
            has_ios_app: currentUser.hasIOSApp,
            has_android_app: currentUser.hasAndroidApp,
            cohort: currentUser.cohort,
            grandfathered: currentUser.grandfathered,
            subscription_status: subscription?.status,
            subscription_plan: subscription?.plan,
            subscription_access_level: subscription?.accessLevel,
            study_reminders: currentUser.studyReminders,
            subscribed_to_emails: currentUser.subscribedToEmails,
          });

          // Start Recording user session for Authenticated Users
          if (
            !currentUser.email.includes('jctest') &&
            !currentUser.email.includes('kbtest') &&
            !currentUser.email.includes('rptest') &&
            !currentUser.email.includes('jacob.caccamo') &&
            !currentUser.email.includes('ryanllewellynphelps') &&
            !currentUser.email.includes('admin@stanford.edu') &&
            !currentUser.email.includes('admin@ucsf.edu') &&
            !currentUser.email.includes('craiglbphelps@gmail.com') &&
            !currentUser.email.includes('ryanrlphelps') &&
            !currentUser.email.includes('losaltoslandscaping.com') &&
            !currentUser.email.includes('kaycbas') &&
            !currentUser.email.includes('kevin@synaptiq.co') &&
            !currentUser.email.includes('jacob@synaptiq.co') &&
            !currentUser.email.includes('ryan@synaptiq.co') &&
            !currentUser.email.includes('kevin@oraai.com') &&
            !currentUser.email.includes('jacob@oraai.com') &&
            !currentUser.email.includes('ryan@oraai.com') &&
            !currentUser.email.includes('vkbzs8xkvd@privaterelay.appleid.com') // Jake's Apple Account
          ) {
            posthog.startSessionRecording();
          }
        } else {
          console.log('Posthog Not Initialized');
          if (cookiesAccepted === 'rejected') {
            console.log('Non Essential Cookies Rejected');
          }
        }
      }
    };
    const isImpersonating = !!getOriginalAdminSession();
    if (!isImpersonating) {
      setupPosthogUserFeatures();
    }
  }, [currentUser]);

  useEffect(() => {
    window.Intercom('shutdown');

    if (currentUser && currentUser.email) {
      window.Intercom('boot', {
        api_base: 'https://api-iam.intercom.io',
        app_id: 'x6oeppkc',
        source: 'ora',
        name: currentUser.name || '',
        email: currentUser.email,
        user_id: currentUser.id,
        created_at: currentUser.createdAt,
      });

      fetchGeneralUserStats();

      if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios') {
        initializePushNotifications(currentUser.id);
        if (!currentUser.hasIOSApp) {
          userOnApp('ios');
        }
      } else if (
        Capacitor.isNativePlatform() &&
        Capacitor.getPlatform() === 'android'
      ) {
        if (!currentUser.hasAndroidApp) {
          userOnApp('android');
        }
      }
    } else {
      window.Intercom('boot', {
        api_base: 'https://api-iam.intercom.io',
        app_id: 'x6oeppkc',
        source: 'ora',
      });
    }

    if (currentUser && currentUser.demoMode) {
      document.body.classList.add('DEMO-MODE');
    } else {
      document.body.classList.remove('DEMO-MODE');
    }
  }, [currentUser]);

  // Set Theme
  useEffect(() => {
    if (
      currentUser &&
      (currentUser.theme === 'dark' ||
        (currentUser.theme === 'system' && systemTheme === 'dark')) &&
      location.pathname !== '/study/qbank/block'
    ) {
      document.body.classList.add('theme-dark');
    } else {
      if (
        !currentUser &&
        (location.pathname.includes('/help') ||
          location.pathname.includes('/library') ||
          location.pathname.includes('/roadmap'))
      ) {
        return;
      }
      if (
        location.pathname === '/pricing' ||
        location.pathname === '/auth' ||
        location.pathname === '/study/summary' ||
        location.pathname === '/onboarding'
      ) {
        document.body.classList.add('theme-dark');
      } else {
        document.body.classList.remove('theme-dark');
      }
    }
  }, [currentUser, location.pathname, systemTheme]);

  // Add classes based on platform
  if (Capacitor.getPlatform() === 'ios') {
    document.body.classList.add('CAPACITOR-IOS');
  }
  if (Capacitor.getPlatform() === 'android') {
    document.body.classList.add('CAPACITOR-ANDROID');
  }
  if (Capacitor.isNativePlatform()) {
    document.body.classList.add('IS-CAPACITOR');
  }

  const userOnApp = async (platformType: 'ios' | 'android') => {
    let updateInfo = {};
    if (platformType === 'ios') {
      updateInfo = { hasIOSApp: true };
    } else {
      updateInfo = { hasAndroidApp: true };
    }
    await updateCurrentUserInfo(updateInfo);
  };

  return (
    <SubscriptionProvider>
      <LocalizationProvider dateAdapter={AdapterMoment}>
        <ThemeProvider
          theme={
            currentUser &&
            (currentUser.theme === 'dark' ||
              (currentUser.theme === 'system' && systemTheme === 'dark'))
              ? darkTheme
              : theme
          }
        >
          <GamepadProvider>
            <RouterProvider router={router} />
          </GamepadProvider>
          <KeywordPopoversManager />
        </ThemeProvider>
      </LocalizationProvider>
    </SubscriptionProvider>
  );
}

export default App;
